import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import {
  Sparkles,
  GraduationCap,
  Plane,
  Heart,
  Coffee,
  ArrowRight,
  CheckCircle,
} from 'lucide-react';

const blocks = [
  {
    icon: GraduationCap,
    title: 'Core Identity',
    description: 'Your era, education, and generation',
    color: 'primary',
  },
  {
    icon: Plane,
    title: 'Migration Journey',
    description: 'Your path from India to abroad',
    color: 'accent',
  },
  {
    icon: Heart,
    title: 'Interests & Hobbies',
    description: 'Sports, music, entertainment',
    color: 'twins',
  },
  {
    icon: Coffee,
    title: 'Lifestyle & Values',
    description: 'How you connect and what matters',
    color: 'strong',
  },
];

export default function Welcome() {
  const { user } = useAuth();

  return (
    <div className="max-w-2xl mx-auto py-8">
      {/* Welcome Header */}
      <div className="text-center mb-12">
        <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-accent-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-primary-500/30">
          <Sparkles className="w-8 h-8 text-white" />
        </div>
        <h1 className="text-3xl font-display font-bold mb-3">
          Welcome, {user?.name?.split(' ')[0] || 'Friend'}!
        </h1>
        <p className="text-gray-600 text-lg">
          Let's build your Identity Board to help you find your people.
        </p>
      </div>

      {/* What is Identity Board */}
      <div className="card p-6 mb-8">
        <h2 className="text-lg font-semibold mb-3">What is an Identity Board?</h2>
        <p className="text-gray-600 mb-4">
          Your Identity Board captures what makes you unique - your background,
          experiences, and interests. It helps WeDesi serve you better:
        </p>
        <ul className="space-y-3 mb-4">
          <li className="flex items-start gap-2 text-gray-600">
            <CheckCircle className="w-5 h-5 text-twins flex-shrink-0 mt-0.5" />
            <span><strong>Find Your People</strong> - Match with Indians who share your story, era, and interests</span>
          </li>
          <li className="flex items-start gap-2 text-gray-600">
            <CheckCircle className="w-5 h-5 text-twins flex-shrink-0 mt-0.5" />
            <span><strong>Discover Events</strong> - See festivals, gatherings, and activities that match your taste</span>
          </li>
          <li className="flex items-start gap-2 text-gray-600">
            <CheckCircle className="w-5 h-5 text-twins flex-shrink-0 mt-0.5" />
            <span><strong>Find Services</strong> - Get recommendations for providers who understand your needs</span>
          </li>
        </ul>
        <div className="flex items-start gap-3 p-3 bg-primary-50 rounded-lg">
          <Sparkles className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-primary-700">
            <strong>Complete 3 of 4 blocks</strong> to unlock Discovery and start
            seeing your matches!
          </p>
        </div>
      </div>

      {/* Blocks Preview */}
      <div className="space-y-3 mb-10">
        <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">
          Your 4 Blocks
        </h3>
        <div className="grid grid-cols-2 gap-3">
          {blocks.map((block) => {
            const Icon = block.icon;
            const colorClasses: Record<string, string> = {
              primary: 'bg-primary-100 text-primary-600',
              accent: 'bg-accent-100 text-accent-600',
              twins: 'bg-twins-light text-twins-dark',
              strong: 'bg-strong-light text-strong-dark',
            };
            return (
              <div key={block.title} className="card p-4 flex items-start gap-3">
                <div className={`w-10 h-10 rounded-lg ${colorClasses[block.color]} flex items-center justify-center flex-shrink-0`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="font-medium">{block.title}</h4>
                  <p className="text-sm text-gray-500">{block.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* CTA */}
      <div className="text-center">
        <Link
          to="/onboarding"
          className="btn-primary inline-flex items-center gap-2 text-lg px-8 py-4 rounded-xl shadow-lg shadow-primary-500/30"
        >
          Get Started
          <ArrowRight className="w-5 h-5" />
        </Link>
        <p className="mt-4 text-sm text-gray-500">
          Quick onboarding + Identity Board takes about 5 minutes
        </p>
      </div>
    </div>
  );
}
